import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import api from 'helpers/api_helper';

export interface ReferralPayload {
  agent_id: number;
  expires_at: string; // ISO string
  max_uses: number;
}

export interface ReferralData {
  id: number;
  code: string;
  link_slug: string;
  agent: number;
  agent_name: string;
  subagent: number | null;
  expires_at: string;
  max_uses: number;
  uses_count: number;
  is_active: boolean;
  created_by: number;
  created_by_name: string;
  created_at: string;
  link: string;
  is_expired: boolean;
  is_usable: boolean;
}

interface ReferralState {
  loading: boolean;
  error: string | null;
  lastCreated: ReferralData | null;
  items: ReferralData[];
}

const STORAGE_KEY = 'referrals';
const loadStored = (): ReferralData[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ReferralData[]) : [];
  } catch {
    return [];
  }
};
const saveStored = (items: ReferralData[]) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch {}
};

const initialState: ReferralState = {
  loading: false,
  error: null,
  lastCreated: null,
  items: loadStored(),
};

export const createReferral = createAsyncThunk<ReferralData, ReferralPayload>(
  'referral/create',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await api.post('/referrals/', payload);
      // API returns { success, message, data }
      return res.data.data as ReferralData;
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Failed to create referral';
      return rejectWithValue(message) as any;
    }
  }
);

// No GET endpoint; we'll persist locally so data survives refresh

const referralSlice = createSlice({
  name: 'referral',
  initialState,
  reducers: {
    clearReferralError(state) { state.error = null; },
    clearLastCreated(state) { state.lastCreated = null; },
    removeReferralById(state, action: PayloadAction<number>) {
      state.items = state.items.filter(r => r.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createReferral.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createReferral.fulfilled, (state, action: PayloadAction<ReferralData>) => {
        state.loading = false;
        state.lastCreated = action.payload;
        state.items = [action.payload, ...state.items];
        saveStored(state.items);
      })
      .addCase(createReferral.rejected, (state, action: any) => {
        state.loading = false;
        state.error = action.payload || 'Failed to create referral';
      })
      ;
  }
});

export const { clearReferralError, clearLastCreated, removeReferralById } = referralSlice.actions;
export default referralSlice.reducer;


